import {Translation} from '../../../_models/translation';
import {environment} from '../../../environments/environment';
import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, map, Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {normalizeLocale, splitLocalePrefix} from '../../_shared/locale/detect-locale';

const LANG_STORAGE_KEY = 'app_language';

@Injectable({providedIn: 'root'})
export class TranslationsService {
  translationsUrl = environment.apiUrl + '/translations';
  defaultpath = '/assets/language/';

  // Locale wykryty server-side z requestu. Ustawiany WYLACZNIE na SSR przez
  // server-side APP_INITIALIZER (app.server.module.ts) zanim cokolwiek odczyta
  // savedLang. W przegladarce pozostaje null => zrodlem jest localStorage.
  // @angular/ssr v21 NIE wstrzykuje per-request providerow przez handle(), wiec
  // dawny token REQUEST_LOCALE byl martwy; teraz locale wyznacza initializer z
  // framework tokenu REQUEST i wstrzykuje je tutaj przez setServerLocale().
  private requestLocale: string | null = null;

  // Zrodlo prawdy dla poczatkowego jezyka w PRZEGLADARCE (T7, PLAN-003):
  //    SCIEZKA (/en/**) -> ?lang= -> localStorage -> 'pl'
  // Bez czlonu "sciezka" przegladarka po hydracji nadpisywala angielski render
  // polskim (localStorage='pl'), czyli adres /en/** byl prawdziwy tylko przez ~1 s.
  // localStorage ZOSTAJE w lancuchu (H7): ktos, kto wybral EN na bezprefiksowym
  // /menu, dalej dostaje EN — adres po prostu bije ten wybor, gdy cos mowi.
  //
  // 🔴 UWAGA: ten lancuch jest ZGODNY z serwerowym na dwoch pierwszych czlonach,
  // ale NIE jest z nim identyczny. SSR (detectLocaleFromRequestLike) ma po FIX 2b:
  //    SCIEZKA -> ?lang= -> 'pl'
  // Wczesniej trzecim czlonem SSR bylo ACCEPT-LANGUAGE — czlon, ktorego przegladarka
  // nie ma i miec nie moze (JS nie widzi tego naglowka; navigator.language to inna
  // dana). Skutkiem byl MIERZALNY rozjazd: '/menu' z przegladarki en-US renderowalo
  // sie na SSR po ANGIELSKU (<html lang="en">), a po hydracji przeskakiwalo na polski
  // (zmierzone na main, na dv1 i na tej galezi). FIX 2b wyciął naglowek: adres jest
  // jedynym sygnalem jezyka, bezprefiksowy adres jest polski dla kazdego.
  // Rozjazdem POZOSTAJE localStorage (H7) — wylacznie dla osoby, ktora sama kliknela
  // przelacznik. Cale to zachowanie jest przypiete w locale-chain-divergence.spec.ts.
  private get savedLang(): string {
    const fromUrl = this.localeFromUrl();
    if (fromUrl) {
      return fromUrl;
    }
    try {
      return localStorage.getItem(LANG_STORAGE_KEY) || 'pl';
    } catch {
      // SSR (brak localStorage) lub private mode — uzyj locale z requestu jesli jest.
      return this.requestLocale || 'pl';
    }
  }

  /**
   * Locale wprost z adresu przegladarki: prefiks sciezki, potem ?lang=.
   * Zwraca null, gdy adres nic nie mowi (albo gdy nie ma DOM — SSR/testy).
   *
   * Parser jest JEDEN — splitLocalePrefix()/normalizeLocale() z detect-locale.ts,
   * tych samych uzywa LocaleUrlSerializer i server-side APP_INITIALIZER. Drugie
   * parsowanie prefiksu w tym pliku byloby gwarantowanym rozjazdem.
   *
   * Czytamy globalny `document`, a nie DI: ten getter wola inicjalizator pola
   * (chosenLanguage = new BehaviorSubject(this.savedLang)), ktory przy
   * useDefineForClassFields odpala sie PRZED przypisaniem wstrzyknietych pol.
   * Na SSR globalny `document` nie istnieje -> null -> dziala requestLocale.
   */
  private localeFromUrl(): 'pl' | 'en' | null {
    try {
      const loc = typeof document === 'undefined' ? null : document.location;
      if (!loc) {
        return null;
      }
      const fromPath = splitLocalePrefix(loc.pathname).locale;
      if (fromPath) {
        return fromPath;
      }
      return normalizeLocale(new URLSearchParams(loc.search).get('lang'));
    } catch {
      return null;
    }
  }

  /**
   * Ustawia locale wykryty server-side (?lang/Accept-Language) i synchronizuje
   * caly stan jezyka: requestLocale (zrodlo savedLang na SSR), chosenLanguage
   * (zasila getLanguage() i loopback /api/...?lang=), path (assety jezyka).
   * Wolane TYLKO przez server-side APP_INITIALIZER PRZED pierwszym renderem.
   * NIE pisze do localStorage (SSR) i nie psuje sciezki browser/admin.
   */
  setServerLocale(locale: string): void {
    this.requestLocale = locale;
    this.chosenLanguage.next(locale);
    this.path.next(this.defaultpath + locale + '/');
  }

  chosenLanguage = new BehaviorSubject<string>(this.savedLang);
  path = new BehaviorSubject<string>(this.defaultpath + this.savedLang + '/');

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService
  ) {
    this.translateService.addLangs(['pl', 'en']);
    this.translateService.setDefaultLang('pl');
    this.translateService.use(this.savedLang);
  }

  getTranslations(): Observable<Record<string, string>> {
    return this.httpClient.get<Translation[]>(this.translationsUrl).pipe(
      map((translations) => {
        return translations.reduce((prev, curr) => {
          prev[curr.translationKey] = curr.translationValue;
          return prev;
        }, {} as Record<string, string>);
      })
    );
  }

  getLanguageAssetPath(): Observable<string> {
    return this.path.asObservable();
  }

  getLanguage(): Observable<string> {
    return this.chosenLanguage.asObservable();
  }

  setLanguage(language: string) {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, language);
    } catch { /* SSR or private mode */ }
    this.chosenLanguage.next(language);
    this.path.next(this.defaultpath + language + '/');
    this.translateService.use(language);
  }

  /**
   * Utrwala wybor jezyka BEZ zmiany czegokolwiek w runtime (T8). Uzywa tego
   * przelacznik <a href>, po ktorym i tak nastepuje pelne przeladowanie dokumentu:
   * gdyby zapisowi towarzyszylo translateService.use(), to przy Ctrl/Cmd+kliku
   * (otwarcie w nowej karcie, bez nawigacji tutaj) biezaca karta zostalaby z
   * polskim tekstem pod angielskim adresem — czyli dokladnie z tym bledem,
   * ktory ta zmiana usuwa.
   */
  rememberLanguage(language: string): void {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, language);
    } catch { /* SSR or private mode */ }
  }

  // Wymusza język tylko w runtime (ngx-translate), BEZ zapisu do localStorage
  // i bez zmiany chosenLanguage. Używane w panelu admina, który ma być zawsze
  // po polsku, tak aby nie nadpisać wyboru języka strony publicznej.
  useRuntimeLanguage(language: string) {
    this.translateService.use(language);
  }

  // Przywraca język zapisany przez użytkownika (po wyjściu z admina).
  restoreSavedLanguage() {
    this.translateService.use(this.savedLang);
  }
}
